import {
  formatError,
  login,
  saveTokenInLocalStorage,
  signUp,
  changePassword,
  requestPasswordReset,
  resetPassword
} from '../../services/AuthService';

// Add axios for getMe API
import {axiosInstanceV2} from '../../services/AxiosInstance.js'; // Adjust path as needed

export const SIGNUP_CONFIRMED_ACTION = '[signup action] confirmed signup';
export const SIGNUP_FAILED_ACTION = '[signup action] failed signup';
export const LOGIN_CONFIRMED_ACTION = '[login action] confirmed login';
export const LOGIN_FAILED_ACTION = '[login action] failed login';
export const LOADING_TOGGLE_ACTION = '[Loading action] toggle loading';
export const LOGOUT_ACTION = '[Logout action] logout action';
export const NAVTOGGLE = 'NAVTOGGLE';
export const PASSWORD_CHANGE_REQUIRED = '[auth action] password change required';
export const PASSWORD_CHANGE_SUCCESS = '[auth action] password change success';
export const PASSWORD_CHANGE_FAILED = '[auth action] password change failed';
export const PASSWORD_RESET_REQUEST_SUCCESS = '[auth action] password reset request success';
export const PASSWORD_RESET_REQUEST_FAILED = '[auth action] password reset request failed';
export const PASSWORD_RESET_SUCCESS = '[auth action] password reset success';
export const PASSWORD_RESET_FAILED = '[auth action] password reset failed';

// Enhanced getMe API call with retry logic
const fetchUserProfile = async (retries = 3) => {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await axiosInstanceV2.get('/auth/me');
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
    } catch (error) {
      if (i === retries - 1) {
        throw error;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
  return null;
};

export function signupAction(mobileNumber, email, password, firstName, lastName, navigate) {
  return (dispatch) => {
    dispatch(loadingToggleAction(true));
    signUp(mobileNumber, email, password, firstName, lastName, navigate)
      .then((response) => {
        dispatch(loadingToggleAction(false));
        if (!response.isError) {
          dispatch(confirmedSignupAction('Registration successful! Please login.'));
        }
      })
      .catch((error) => {
        dispatch(loadingToggleAction(false));
        let errorMessage = 'Registration failed';
        if (error.response && error.response.data) {
          errorMessage = formatError(error.response.data);
        } else if (error.message) {
          errorMessage = error.message;
        }
        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
        dispatch(signupFailedAction(errorMessage));
      });
  };
}

export function Logout(navigate) {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('userDetails');
  localStorage.removeItem('refreshToken');
  navigate('/login');

  return {
    type: LOGOUT_ACTION,
  };
}

// Enhanced loginAction with proper synchronization
export function loginAction(email, password, navigate, updateUser, setIsLoading) {
  return async (dispatch) => {
    try {
      dispatch(loadingToggleAction(true));
      
      const response = await login(email, password);

      if (response && !response.isError) {
        // Save tokens first
        const accessToken = response.data?.token || '';
        const refreshToken = response.data?.refreshToken || '';
        
        localStorage.setItem('accessToken', accessToken);
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }

        // Ensure axios uses the new token immediately
        if (axiosInstanceV2.defaults.headers) {
          axiosInstanceV2.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        }

        // MODIFIED: Navigate to loading page immediately
        if (navigate) {
          navigate('/loading');
        }

        try {
          // Fetch complete user profile
          const completeUserData = await fetchUserProfile();
          
          if (completeUserData && completeUserData.id) {
            // Use synchronous update function from context
            if (updateUser) {
              updateUser(completeUserData);
            }

            // Update Redux state
            dispatch(
              loginConfirmedAction({
                email: completeUserData.email || email,
                idToken: accessToken,
                localId: completeUserData.id,
                id: completeUserData.id,
                expiresIn: response.data?.expireIn || '',
                refreshToken: refreshToken,
                isSuperAdmin: completeUserData.isSuperAdmin || false,
                profileIds: completeUserData.profileIds || '',
                profiles: completeUserData.profiles || [],
              })
            );

            dispatch(loadingToggleAction(false));
            if (setIsLoading) setIsLoading(false);
            
            // MODIFIED: Navigate to dashboard after delay
            setTimeout(() => {
              if (navigate) {
                navigate('/placement-dashboard');
              }
            }, 1500); // 1.5 second delay to match lms-ui
            
          } else {
            throw new Error('Invalid user profile data received');
          }
          
        } catch (profileError) {
          // Fallback: use basic user data from login response
          const basicUserData = {
            id: response.data?.user?.id || '',
            email: email,
            isSuperAdmin: response.data?.user?.isSuperAdmin || false,
            profileIds: response.data?.user?.profileIds || '',
            profiles: [],
            firstName: response.data?.user?.firstName || '',
            lastName: response.data?.user?.lastName || '',
            mobileNumber: response.data?.user?.mobileNumber || '',
          };
          
          if (updateUser) {
            updateUser(basicUserData);
          }
          
          dispatch(loadingToggleAction(false));
          if (setIsLoading) setIsLoading(false);
          
          // Navigate after delay
          setTimeout(() => {
            if (navigate) {
              navigate('/placement-dashboard');
            }
          }, 1500);
        }

      } else if (response && response.isError) {
        dispatch(loadingToggleAction(false));
        if (setIsLoading) setIsLoading(false);
        
        // Check for password change required
        if (response.message && 
            (response.message.code === 'AUE014' || 
             response.message.message === 'You must change your password before logging in.')) {
          dispatch(passwordChangeRequiredAction({
            email: email,
            message: formatError(response)
          }));
        } else {
          const errorMsg = response?.error || response?.message || 'Login failed';
          dispatch(loginFailedAction(typeof errorMsg === 'string' ? errorMsg : formatError(response)));
        }
      } else {
        dispatch(loadingToggleAction(false));
        if (setIsLoading) setIsLoading(false);
        dispatch(loginFailedAction('Login failed'));
      }
    } catch (error) {
      dispatch(loadingToggleAction(false));
      if (setIsLoading) setIsLoading(false);
      
      let errorMessage = 'Login failed';
      if (error.response && error.response.data) {
        errorMessage = formatError(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      if (typeof errorMessage === 'object') {
        errorMessage = errorMessage.message || JSON.stringify(errorMessage);
      }
      
      dispatch(loginFailedAction(String(errorMessage)));
    }
  };
}

export function passwordChangeRequiredAction(data) {
  return {
    type: PASSWORD_CHANGE_REQUIRED,
    payload: data,
  };
}

export function changePasswordAction(currentPassword, newPassword, email, navigate) {
  return async (dispatch) => {
    try {
      dispatch(loadingToggleAction(true));
      const response = await changePassword(currentPassword, newPassword, email);
      
      dispatch(loadingToggleAction(false));
      
      if (!response.isError) {
        dispatch(passwordChangeSuccessAction('Password changed successfully'));
        setTimeout(() => {
          if (navigate) {
            navigate('/login');
          }
        }, 2000);
      } else {
        dispatch(passwordChangeFailedAction(formatError(response)));
      }
    } catch (error) {
      dispatch(loadingToggleAction(false));
      let errorMessage = 'Password change failed';
      if (error.response && error.response.data) {
        errorMessage = formatError(error.response.data);
      }
      dispatch(passwordChangeFailedAction(errorMessage));
    }
  };
}

export function passwordChangeSuccessAction(message) {
  return {
    type: PASSWORD_CHANGE_SUCCESS,
    payload: String(message),
  };
}

export function passwordChangeFailedAction(message) {
  return {
    type: PASSWORD_CHANGE_FAILED,
    payload: String(message),
  };
}

export function requestPasswordResetAction(email) {
  return async (dispatch) => {
    try {
      dispatch(loadingToggleAction(true));
      const response = await requestPasswordReset(email);
      
      dispatch(loadingToggleAction(false));
      
      if (!response.isError) {
        dispatch(passwordResetRequestSuccessAction('Password reset email sent successfully'));
      } else {
        dispatch(passwordResetRequestFailedAction(formatError(response)));
      }
    } catch (error) {
      dispatch(loadingToggleAction(false));
      let errorMessage = 'Failed to send reset email';
      if (error.response && error.response.data) {
        errorMessage = formatError(error.response.data);
      }
      dispatch(passwordResetRequestFailedAction(errorMessage));
    }
  };
}

export function passwordResetRequestSuccessAction(message) {
  return {
    type: PASSWORD_RESET_REQUEST_SUCCESS,
    payload: String(message),
  };
}

export function passwordResetRequestFailedAction(message) {
  return {
    type: PASSWORD_RESET_REQUEST_FAILED,
    payload: String(message),
  };
}

export function resetPasswordAction(token, newPassword, navigate) {
  return async (dispatch) => {
    try {
      dispatch(loadingToggleAction(true));
      const response = await resetPassword(token, newPassword);
      
      dispatch(loadingToggleAction(false));
      
      if (!response.isError) {
        dispatch(passwordResetSuccessAction('Password reset successfully'));
        setTimeout(() => {
          if (navigate) {
            navigate('/login');
          }
        }, 2000);
      } else {
        dispatch(passwordResetFailedAction(formatError(response)));
      }
    } catch (error) {
      dispatch(loadingToggleAction(false));
      let errorMessage = 'Password reset failed';
      if (error.response && error.response.data) {
        errorMessage = formatError(error.response.data);
      }
      dispatch(passwordResetFailedAction(errorMessage));
    }
  };
}

export function passwordResetSuccessAction(message) {
  return {
    type: PASSWORD_RESET_SUCCESS,
    payload: String(message),
  };
}

export function passwordResetFailedAction(message) {
  return {
    type: PASSWORD_RESET_FAILED,
    payload: String(message),
  };
}

export function loginFailedAction(data) {
  return {
    type: LOGIN_FAILED_ACTION,
    payload: String(data),
  };
}

export function loginConfirmedAction(data) {
  return {
    type: LOGIN_CONFIRMED_ACTION,
    payload: data,
  };
}

export function confirmedSignupAction(payload) {
  return {
    type: SIGNUP_CONFIRMED_ACTION,
    payload: String(payload),
  };
}

export function signupFailedAction(message) {
  return {
    type: SIGNUP_FAILED_ACTION,
    payload: String(message),
  };
}

export function loadingToggleAction(status) {
  return {
    type: LOADING_TOGGLE_ACTION,
    payload: status,
  };
}

export const navtoggle = () => {
  return {
    type: 'NAVTOGGLE',
  };
};

